import { useState, useCallback, useEffect } from 'react';
import { OnboardingStepLayout, EsporaiInput, EsporaiButton } from 'components';
import { useAuth } from 'components/context/AuthContext';
import { authApi, logApi } from 'api';

const { createLog } = logApi;

const OnboardingValidateEmailStep = ({ onValidation }) => {
  const TIMEOUT_SECONDS = 20; 
  const { auth, logout } = useAuth();
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [hasStartedTyping, setHasStartedTyping] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [timer, setTimer] = useState(TIMEOUT_SECONDS);
  const [isResending, setIsResending] = useState(false);

  const validateLocal = useCallback(() => {
    if (!code) {
      setError('El código de confirmación es requerido');
      return false;
    }
    if (code.length !== 4) {
      setError('El código debe tener 4 dígitos');
      return false;
    }
    setError(null);
    return true;
  }, [code]);

  const handleSubmit = async () => {
    if (!validateLocal()) return;
    // Reset states before starting verification
    setError(null);
    setIsVerifying(true);
    try {
      await authApi.verifyEmail(code);
      onValidation(true);
    } catch (apiError) {
      console.error('Error al verificar el código:', apiError);
      const errorMessage = apiError?.response?.data?.error || 'Error al verificar el código';
      setError(errorMessage);
      setInputFocused(true);
      onValidation(false, errorMessage);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (timer > 0 || isResending) return;
    setIsResending(true);
    try {
      await authApi.requestVerificationCode();
      createLog({
        level: 'info',
        message: 'Reenvió código de verificación'
      });
      setTimer(TIMEOUT_SECONDS);
      setError(null);
    } catch (resendError) {
      const errorMessage = resendError?.response?.data?.error || 'Error al reenviar el código';
      setError(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  const handleInputChange = (value) => {
    const sanitizedValue = value.replace(/[^0-9]/g, '').slice(0, 4);
    if (!hasStartedTyping && sanitizedValue) {
      setHasStartedTyping(true);
    }
    setCode(sanitizedValue);
  };

  const handleFocus = () => {
    setInputFocused(true);
  };

  const handleBlur = () => {
    setInputFocused(false);
  };

  const displayError = inputFocused && hasStartedTyping ? error : null;

  useEffect(() => {
    if (timer > 0) {
      const intervalId = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
      return () => clearInterval(intervalId);
    }
  }, [timer]);

  return (
    <OnboardingStepLayout
      title="Revisá tu email"
      subtitle={
        auth?.user?.email
          ? <>Te enviamos un código de 4 dígitos a <strong>{auth.user.email}</strong>. Revisá tu casilla (incluso spam) e ingresalo acá abajo.</>
          : "Colocá el código de confirmación para avanzar con el registro."
      }
    >
      <EsporaiInput
        autoFocus
        type="text"
        value={code}
        onChange={handleInputChange}
        placeholder="CÓDIGO"
        error={displayError}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={isVerifying}
      />

      <div style={{ marginTop: '1rem', textAlign: 'center' }}>
        <EsporaiButton
          type="button"
          onClick={handleSubmit}
          disabled={code.length !== 4 || isVerifying}
          variant="primary"
          className="w-full"
        >
          {isVerifying ? 'Validando...' : 'Siguiente'}
        </EsporaiButton>
        <EsporaiButton
            type="button"
            onClick={logout}
            variant="outline"
            className="w-full mt-2"
          >
            Cerrar Sesión
          </EsporaiButton>
      </div>

      <div style={{ marginTop: '1rem', textAlign: 'center' }}>
        {timer > 0 ? (
          <span>Podés solicitar otro código en {timer} segundos.</span>
        ) : (
          <button
            type="button"
            onClick={handleResendCode}
            disabled={isResending}
            className="w-full text-blue-500 underline bg-transparent border-none cursor-pointer"
          >
            {isResending ? 'Reenviando...' : 'Reenviar código'}
          </button>
        )}
      </div>
    </OnboardingStepLayout>
  );
};

export default OnboardingValidateEmailStep;
